/**
 * Liquid query builder — pure functions that produce the LiquidJS expression
 * persisted to `sendergroup.userAttrs`.
 *
 * Backend matcher (`mailapi/models/sendergroup.js:matchesDynamicTemplate`) accepts
 * either a bare expression (auto-wrapped in `{{ ... }}`) or an explicit Liquid
 * block. We emit the bare expression form so the raw editor and the stored
 * value stay free of `{{ ... }}` plumbing (the backend wraps it at evaluation
 * time). LiquidJS stringifies the boolean result, and the matcher counts a user
 * as a member when the rendered output is the literal string `"true"`.
 */

export const OPERATORS = [
  { id: 'contains', labelKey: 'label.OPERATOR_CONTAINS' },
  { id: 'not_contains', labelKey: 'label.OPERATOR_NOT_CONTAINS' },
  { id: 'equals', labelKey: 'label.OPERATOR_EQUALS' },
  { id: 'not_equals', labelKey: 'label.OPERATOR_NOT_EQUALS' }
];

export const JOINS = [
  { id: 'and', labelKey: 'label.JOIN_AND' },
  { id: 'or', labelKey: 'label.JOIN_OR' }
];

const generateClauseId = () => `clause-${Math.random().toString(36).slice(2, 10)}`;

export const createEmptyClause = () => ({
  _id: generateClauseId(),
  variable: '',
  operatorId: 'contains',
  value: ''
});

export const createEmptyState = () => ({
  clauses: [createEmptyClause()],
  joins: []
});

export const isStateSubmittable = (state) => {
  if (!state || !Array.isArray(state.clauses) || state.clauses.length === 0) {
    return false;
  }

  return state.clauses.every((clause) => !!clause?.variable && !!clause?.value);
};

// Escape backslashes first, then single quotes, so the value survives Liquid's
// string-literal parsing. A lone trailing backslash would otherwise escape the
// closing quote and throw a parse error (the backend then counts every user as
// a non-member, silently emptying the group).
export const escapeSingleQuotes = (value) =>
  String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'");

export const renderClause = (clause) => {
  if (!clause?.variable || !clause?.value) {
    return '';
  }

  const escaped = escapeSingleQuotes(clause.value);

  switch (clause.operatorId) {
    case 'contains':
      return `fields.${clause.variable} contains '${escaped}'`;
    case 'not_contains':
      return `not fields.${clause.variable} contains '${escaped}'`;
    case 'equals':
      return `fields.${clause.variable} == '${escaped}'`;
    case 'not_equals':
      return `fields.${clause.variable} != '${escaped}'`;
    default:
      return '';
  }
};

export const renderExpression = (state) => {
  if (!state || !Array.isArray(state.clauses)) {
    return '';
  }

  const survivors = [];

  for (let originalIndex = 0; originalIndex < state.clauses.length; originalIndex++) {
    const rendered = renderClause(state.clauses[originalIndex]);
    if (rendered) {
      survivors.push({ rendered, originalIndex });
    }
  }

  if (survivors.length === 0) {
    return '';
  }

  const joins = Array.isArray(state.joins) ? state.joins : [];
  let body = survivors[0].rendered;

  for (let i = 1; i < survivors.length; i++) {
    // The join visible in the UI immediately above a surviving clause is
    // joins[originalIndex - 1] — i.e. the join slot to the left of that clause.
    // When a middle clause is empty, this preserves the join the admin sees
    // attached to the surviving clause and drops the join attached to the
    // empty one.
    const joinSlot = survivors[i].originalIndex - 1;
    const join = joins[joinSlot] || 'and';
    body += ` ${join} ${survivors[i].rendered}`;
  }

  return body;
};

// Human-readable operator phrasing for the effective-grouping description.
const GROUPING_OPERATOR_PHRASES = {
  contains: 'contains',
  not_contains: 'does not contain',
  equals: 'is',
  not_equals: 'is not'
};

const describeClause = (clause) => {
  const phrase = GROUPING_OPERATOR_PHRASES[clause.operatorId] || clause.operatorId;
  return `${clause.variable} ${phrase} "${clause.value}"`;
};

// Build a right-associative fold tree over the surviving clause descriptions and
// their joins: `X1 j1 X2 j2 X3` groups as `X1 j1 (X2 j2 X3)`, mirroring how
// LiquidJS actually evaluates the flat expression (right-to-left, no precedence).
const foldGrouping = (nodes, ops, start) => {
  if (start >= nodes.length - 1) {
    return { leaf: nodes[start] };
  }
  return { op: ops[start], left: nodes[start], right: foldGrouping(nodes, ops, start + 1) };
};

// Print the fold tree with parentheses only where the operator changes —
// consecutive same-operator clauses flatten, since `and`/`or` are each
// associative, so an all-AND or all-OR chain reads without any parentheses.
const printGrouping = (node) => {
  if (node.leaf !== undefined) {
    return node.leaf;
  }
  const op = node.op === 'or' ? 'OR' : 'AND';
  let right;
  if (node.right.leaf !== undefined) {
    right = node.right.leaf;
  } else if (node.right.op === node.op) {
    right = printGrouping(node.right);
  } else {
    right = `(${printGrouping(node.right)})`;
  }
  return `${node.left} ${op} ${right}`;
};

/**
 * Produce a human-readable, parenthesized description of how the builder's flat
 * clause list is actually grouped by LiquidJS (right-to-left, no precedence),
 * e.g. `jobfamilygroup is "Sales" AND (company is "A" OR company is "B")`. Lets
 * the admin see the real grouping before saving. Returns '' when there is
 * nothing to describe.
 *
 * @param {{ clauses: object[], joins: string[] }} state
 * @returns {string}
 */
export const describeGrouping = (state) => {
  if (!state || !Array.isArray(state.clauses)) {
    return '';
  }

  const survivors = [];
  for (let i = 0; i < state.clauses.length; i++) {
    const clause = state.clauses[i];
    if (clause?.variable && clause?.value) {
      survivors.push({ clause, originalIndex: i });
    }
  }
  if (survivors.length === 0) {
    return '';
  }

  const joins = Array.isArray(state.joins) ? state.joins : [];
  const nodes = survivors.map(({ clause }) => describeClause(clause));
  const ops = [];
  for (let k = 1; k < survivors.length; k++) {
    // Same join-slot mapping as renderExpression: the join above a surviving
    // clause is joins[originalIndex - 1].
    ops.push(joins[survivors[k].originalIndex - 1] || 'and');
  }

  return printGrouping(foldGrouping(nodes, ops, 0));
};

// Strip an optional `{{ ... }}` Liquid-output wrapper, returning the trimmed
// inner expression. We now emit bare expressions, but groups saved before this
// change (and anything a user pastes) may still carry the wrapper, so we
// normalize it away before comparing against the bare render.
export const stripWrapper = (expression) => {
  const raw = String(expression ?? '').trim();
  const wrapped = /^\{\{([\s\S]*)\}\}$/.exec(raw);
  return wrapped ? wrapped[1].trim() : raw;
};

// Reverse of escapeSingleQuotes: every escape is a single backslash followed by
// the literal character, so collapsing `\<char>` → `<char>` restores the value.
const unescapeLiquidValue = (value) => String(value ?? '').replace(/\\([\s\S])/g, '$1');

// One clause = optional `not `, a `fields.<var>`, an operator, and a
// single-quoted (backslash-escaped) value. Sticky so we can walk the body
// clause → join → clause without a join inside a quoted value being mistaken
// for a real join.
const CLAUSE_PATTERN = /(not\s+)?fields\.(\S+?)\s+(contains|==|!=)\s+'((?:[^'\\]|\\[\s\S])*)'/y;
const JOIN_PATTERN = /\s+(and|or)\s+/y;

/**
 * Parse a LiquidJS expression back into builder state, or return `null` when it
 * is not expressible in the builder grammar (so the caller keeps raw mode).
 * Inverse of `renderExpression` for builder-generated output.
 *
 * @param {string} expression
 * @returns {{ clauses: object[], joins: string[] } | null}
 */
export const parseExpression = (expression) => {
  const raw = String(expression ?? '').trim();
  if (!raw) {
    return null;
  }

  let body = raw;
  const wrapped = /^\{\{([\s\S]*)\}\}$/.exec(raw);
  if (wrapped) {
    body = wrapped[1].trim();
  } else if (/\{\{|\}\}|\{%|%\}/.test(raw)) {
    // Stray / non-matching template delimiters → not builder-shaped.
    return null;
  }

  if (!body) {
    return null;
  }

  const clauses = [];
  const joins = [];
  let pos = 0;

  for (;;) {
    CLAUSE_PATTERN.lastIndex = pos;
    const match = CLAUSE_PATTERN.exec(body);
    if (!match) {
      return null;
    }

    const negated = !!match[1];
    const variable = match[2];
    const operator = match[3];
    const value = unescapeLiquidValue(match[4]);

    let operatorId;
    if (operator === 'contains') {
      operatorId = negated ? 'not_contains' : 'contains';
    } else if (negated) {
      // The builder never emits `not` with == / != — treat as non-builder.
      return null;
    } else if (operator === '==') {
      operatorId = 'equals';
    } else {
      operatorId = 'not_equals';
    }

    clauses.push({ _id: generateClauseId(), variable, operatorId, value });
    pos = CLAUSE_PATTERN.lastIndex;

    if (pos === body.length) {
      break;
    }

    JOIN_PATTERN.lastIndex = pos;
    const joinMatch = JOIN_PATTERN.exec(body);
    if (!joinMatch) {
      return null;
    }
    joins.push(joinMatch[1]);
    pos = JOIN_PATTERN.lastIndex;
  }

  if (clauses.length === 0 || joins.length !== clauses.length - 1) {
    return null;
  }

  return { clauses, joins };
};

/**
 * Decide which editor mode a persisted expression should open in, and the
 * builder state to seed when it opens in the builder. We default to the
 * builder and only fall back to raw when the expression isn't builder-
 * representable, gated on an exact render round-trip so a parse we can't
 * faithfully reproduce never silently rewrites `userAttrs`.
 *
 * @param {string} attrs
 * @returns {{ mode: 'builder' | 'raw', state: object | null }}
 *   `state` is null when mode is 'raw' (caller keeps its own builder state).
 */
export const resolveBuilderState = (attrs) => {
  const stored = String(attrs ?? '').trim();
  if (!stored) {
    return { mode: 'builder', state: createEmptyState() };
  }

  const parsed = parseExpression(stored);
  // renderExpression now emits bare; strip an optional `{{ ... }}` wrapper off
  // the stored value so legacy wrapped expressions still round-trip cleanly into
  // the builder instead of falling to raw mode.
  if (parsed && renderExpression(parsed) === stripWrapper(stored)) {
    return { mode: 'builder', state: parsed };
  }

  return { mode: 'raw', state: null };
};
